/**
 * Universal Spatial Grid System for TON Survivors
 *
 * This system provides efficient spatial queries for game objects like enemies, bullets, and pickups.
 * It uses a 2D grid to organize objects by their position for O(1) average lookups.
 *
 * @example Enemy Targeting:
 * ```typescript
 * const enemyGrid = new SpatialGrid<Enemy>(100);
 * const nearestEnemy = enemyGrid.findNearest(player.x, player.y, (enemy) => !enemy.isDead);
 * ```
 *
 * @example Collision Detection:
 * ```typescript
 * const bulletGrid = new SpatialGrid<BaseBullet>(80);
 * const collisions = bulletGrid.findIntersections(enemyGrid, 32); // 32px collision radius
 * ```
 */

export interface SpatialObject {
  x: number;
  y: number;
  active?: boolean;
}

export interface SpatialQueryOptions {
  maxResults?: number;
  filterFunction?: (obj: any) => boolean;
  sortByDistance?: boolean;
}

export interface CollisionPair<T, U> {
  objectA: T;
  objectB: U;
  distance: number;
}

export default class SpatialGrid<T extends SpatialObject> {
  private grid: Map<string, T[]> = new Map();
  private readonly cellSize: number;
  private objectCellMap: WeakMap<T, string> = new WeakMap();
  private objectCount: number = 0;

  constructor(cellSize: number = 100) {
    this.cellSize = cellSize;
  }

  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  private getCellCoords(cellKey: string): { x: number; y: number } {
    const [x, y] = cellKey.split(",").map(Number);
    return { x, y };
  }

  /**
   * Add an object to the spatial grid
   */
  addObject(obj: T): void {
    if (obj.active === false) return;

    const cellKey = this.getCellKey(obj.x, obj.y);

    if (!this.grid.has(cellKey)) {
      this.grid.set(cellKey, []);
    }

    const cell = this.grid.get(cellKey)!;
    if (!cell.includes(obj)) {
      cell.push(obj);
      this.objectCellMap.set(obj, cellKey);
      this.objectCount++;
    }
  }

  /**
   * Remove an object from the spatial grid
   */
  removeObject(obj: T): void {
    const cellKey = this.objectCellMap.get(obj);
    if (cellKey) {
      const cell = this.grid.get(cellKey);
      if (cell) {
        const index = cell.indexOf(obj);
        if (index !== -1) {
          cell.splice(index, 1);
          this.objectCount--;
          if (cell.length === 0) {
            this.grid.delete(cellKey);
          }
        }
      }
      this.objectCellMap.delete(obj);
    }
  }

  /**
   * Update object position in the grid
   */
  updateObjectPosition(obj: T, newX: number, newY: number): void {
    const oldCellKey = this.objectCellMap.get(obj);
    const newCellKey = this.getCellKey(newX, newY);

    if (oldCellKey !== newCellKey) {
      this.removeObject(obj);
      // Update object position (external responsibility)
      this.addObject(obj);
    }
  }

  /**
   * Find objects within a radius
   */
  findInRadius(
    x: number,
    y: number,
    radius: number,
    options: SpatialQueryOptions = {}
  ): T[] {
    const {
      maxResults = Infinity,
      filterFunction,
      sortByDistance = false,
    } = options;
    const results: T[] = [];
    const cellRadius = Math.ceil(radius / this.cellSize);
    const centerCellX = Math.floor(x / this.cellSize);
    const centerCellY = Math.floor(y / this.cellSize);
    const radiusSquared = radius * radius;

    for (let dx = -cellRadius; dx <= cellRadius; dx++) {
      for (let dy = -cellRadius; dy <= cellRadius; dy++) {
        const cellKey = `${centerCellX + dx},${centerCellY + dy}`;
        const cell = this.grid.get(cellKey);

        if (cell) {
          for (const obj of cell) {
            if (obj.active === false) continue;
            if (filterFunction && !filterFunction(obj)) continue;

            const distanceSquared = this.getDistanceSquared(x, y, obj.x, obj.y);
            if (distanceSquared <= radiusSquared) {
              results.push(obj);
              if (results.length >= maxResults) {
                return results;
              }
            }
          }
        }
      }
    }

    if (sortByDistance) {
      results.sort((a, b) => {
        const distA = this.getDistanceSquared(x, y, a.x, a.y);
        const distB = this.getDistanceSquared(x, y, b.x, b.y);
        return distA - distB;
      });
    }

    return results;
  }

  /**
   * Find the nearest object to a point
   */
  findNearest(
    x: number,
    y: number,
    filterFunction?: (obj: T) => boolean
  ): T | null {
    const searchRadius = this.cellSize;
    let bestObject: T | null = null;
    let bestDistanceSquared = Infinity;

    // Start with immediate cell, then expand search
    for (let radius = 1; radius <= 5; radius++) {
      const cellRadius = radius;
      const centerCellX = Math.floor(x / this.cellSize);
      const centerCellY = Math.floor(y / this.cellSize);

      for (let dx = -cellRadius; dx <= cellRadius; dx++) {
        for (let dy = -cellRadius; dy <= cellRadius; dy++) {
          // Skip cells we've already checked
          if (Math.abs(dx) < radius && Math.abs(dy) < radius) continue;

          const cellKey = `${centerCellX + dx},${centerCellY + dy}`;
          const cell = this.grid.get(cellKey);

          if (cell) {
            for (const obj of cell) {
              if (obj.active === false) continue;
              if (filterFunction && !filterFunction(obj)) continue;

              const distanceSquared = this.getDistanceSquared(
                x,
                y,
                obj.x,
                obj.y
              );
              if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestObject = obj;
              }
            }
          }
        }
      }

      // If we found something, return it (nearest in current search radius)
      if (bestObject) {
        return bestObject;
      }
    }

    return bestObject;
  }

  /**
   * Find the furthest object from a point
   */
  findFurthest(
    x: number,
    y: number,
    objects: T[],
    prioritizeOffScreen?: boolean
  ): T | null {
    if (objects.length === 0) return null;

    let furthest: T | null = null;
    let maxDistanceSquared = -1;

    for (const obj of objects) {
      if (obj.active === false) continue;

      let distanceSquared = this.getDistanceSquared(x, y, obj.x, obj.y);

      // Prioritize off-screen objects if requested
      if (prioritizeOffScreen && this.isOffScreen && this.isOffScreen(obj)) {
        distanceSquared *= 1.5;
      }

      if (distanceSquared > maxDistanceSquared) {
        maxDistanceSquared = distanceSquared;
        furthest = obj;
      }
    }

    return furthest;
  }

  /**
   * Find intersections between two spatial grids
   */
  findIntersections<U extends SpatialObject>(
    otherGrid: SpatialGrid<U>,
    collisionRadius: number,
    options: SpatialQueryOptions = {}
  ): CollisionPair<T, U>[] {
    const { maxResults = Infinity, filterFunction } = options;
    const collisions: CollisionPair<T, U>[] = [];
    const radiusSquared = collisionRadius * collisionRadius;

    // Iterate through all cells in this grid
    for (const [cellKey, objects] of this.grid) {
      if (objects.length === 0) continue;

      const { x: cellX, y: cellY } = this.getCellCoords(cellKey);
      const cellWorldX = cellX * this.cellSize;
      const cellWorldY = cellY * this.cellSize;

      // Find nearby objects in the other grid
      const nearbyObjects = otherGrid.findInRadius(
        cellWorldX + this.cellSize / 2,
        cellWorldY + this.cellSize / 2,
        collisionRadius + this.cellSize,
        { filterFunction }
      );

      // Check collisions between objects in this cell and nearby objects
      for (const objA of objects) {
        if (objA.active === false) continue;

        for (const objB of nearbyObjects) {
          if (objB.active === false) continue;

          const distanceSquared = this.getDistanceSquared(
            objA.x,
            objA.y,
            objB.x,
            objB.y
          );
          if (distanceSquared <= radiusSquared) {
            collisions.push({
              objectA: objA,
              objectB: objB,
              distance: Math.sqrt(distanceSquared),
            });

            if (collisions.length >= maxResults) {
              return collisions;
            }
          }
        }
      }
    }

    return collisions;
  }

  /**
   * Get all objects in the grid
   */
  getAllObjects(): T[] {
    const allObjects: T[] = [];
    for (const cell of this.grid.values()) {
      for (const obj of cell) {
        if (obj.active !== false) {
          allObjects.push(obj);
        }
      }
    }
    return allObjects;
  }

  /**
   * Clear all objects from the grid
   */
  clear(): void {
    this.grid.clear();
    this.objectCellMap = new WeakMap();
    this.objectCount = 0;
  }

  /**
   * Get grid statistics
   */
  getStats(): {
    cellCount: number;
    objectCount: number;
    averageObjectsPerCell: number;
    maxObjectsInCell: number;
  } {
    const cellCount = this.grid.size;
    let maxObjectsInCell = 0;

    for (const cell of this.grid.values()) {
      maxObjectsInCell = Math.max(maxObjectsInCell, cell.length);
    }

    return {
      cellCount,
      objectCount: this.objectCount,
      averageObjectsPerCell: cellCount > 0 ? this.objectCount / cellCount : 0,
      maxObjectsInCell,
    };
  }

  /**
   * Batch operations for performance
   */
  batchUpdate(objects: T[]): void {
    // Remove all objects first
    this.clear();

    // Add all objects
    for (const obj of objects) {
      this.addObject(obj);
    }
  }

  /**
   * Helper method to check if an object is off-screen
   * This should be overridden by specific implementations
   */
  protected isOffScreen?: (obj: T) => boolean;

  /**
   * Set the off-screen check function
   */
  setOffScreenCheck(checkFunction: (obj: T) => boolean): void {
    this.isOffScreen = checkFunction;
  }

  private getDistanceSquared(
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return dx * dx + dy * dy;
  }
}
